/**
 * System Config Service
 * Manages global system configuration like active academic year,
 * grading policies in use, and feature flags.
 */
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { examDb } from '../../config/firebase';
import { COLLECTIONS } from '../../config/constants';
import auditService from '../audit/auditService';
import { AUDIT_ACTIONS } from '../../config/constants';

const CONFIG_DOC_ID = 'config';

class SystemConfigService {
  constructor() {
    this._cache = null;
    this._cacheTime = 0;
    this._cacheTTL = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Get the current system configuration (with in-memory TTL cache)
   * @returns {Object|null}
   */
  async getConfig() {
    if (this._cache && (Date.now() - this._cacheTime < this._cacheTTL)) {
      return this._cache;
    }
    const docRef = doc(examDb, COLLECTIONS.SYSTEM_CONFIG, CONFIG_DOC_ID);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      this._cache = data;
      this._cacheTime = Date.now();
      return data;
    }
    return null;
  }

  /**
   * Initialize default configuration (used during first setup)
   * @param {Object} defaultConfig 
   * @param {Object} user - User performing the setup
   */
  async initializeConfig(defaultConfig, user) {
    const docRef = doc(examDb, COLLECTIONS.SYSTEM_CONFIG, CONFIG_DOC_ID);
    await setDoc(docRef, {
      ...defaultConfig,
      isSetupComplete: true,
      updatedAt: new Date().toISOString()
    });
    this._cache = null; // Invalidate cache after mutation

    await auditService.log({
      action: AUDIT_ACTIONS.CREATE,
      collection: COLLECTIONS.SYSTEM_CONFIG,
      documentId: CONFIG_DOC_ID,
      performedBy: user.uid,
      performedByName: user.displayName,
      performedByRole: user.role,
      description: 'System configuration initialized',
      newValue: defaultConfig
    });
  }

  /**
   * Update specific configuration settings
   * @param {Object} updates 
   * @param {Object} user - User performing the update
   */
  async updateConfig(updates, user) {
    const docRef = doc(examDb, COLLECTIONS.SYSTEM_CONFIG, CONFIG_DOC_ID);
    
    // Get old config for audit
    const oldConfig = await this.getConfig();

    await setDoc(docRef, {
      ...updates,
      updatedAt: new Date().toISOString()
    }, { merge: true });
    this._cache = null; // Invalidate cache after mutation

    await auditService.log({
      action: AUDIT_ACTIONS.UPDATE,
      collection: COLLECTIONS.SYSTEM_CONFIG,
      documentId: CONFIG_DOC_ID,
      performedBy: user.uid,
      performedByName: user.displayName,
      performedByRole: user.role,
      description: 'System configuration updated',
      oldValue: oldConfig,
      newValue: updates
    });
  }
}

export const systemConfigService = new SystemConfigService();
export default systemConfigService;
